import React from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy: React.FC = () => {
  return (
    <Container fluid className="p-4">
      <h1>Privacy Policy</h1>
      <i>Last updated February 24, 2025</i>
      <p>
        This Privacy Notice for Get Move srl ('we', 'us', or 'our'), describes
        how and why we might access, collect, store, use, and/or share
        ('process') your personal information when you use our services
        ('Services'), including when you:
      </p>
      <ul>
        <li>
          Download and use our mobile application (GetMove), or any other
          application of ours that links to this Privacy Notice
        </li>
        <br />
        <li>
          Use GetMove. This app helps you book and manage your favorite courts,
          join other users’ matches, and create or participate in tournaments.
        </li>
        <br />
        <li>
          Engage with us in other related ways, including any sales, marketing,
          or events.
        </li>
      </ul>
      <strong>Questions or concerns?</strong>
      <p>
        Reading this Privacy Notice will help you understand your privacy rights
        and choices. We are responsible for making decisions about how your
        personal information is processed. If you do not agree with our policies
        and practices, please do not use our Services. If you still have any
        questions or concerns, please contact us at{" "}
        <strong>support@getmoveapp.com</strong>.
      </p>
      <h1>Summary of key points</h1>
      <p>
        This summary provides key points from our Privacy Notice, but you can
        find out more details about any of these topics by clicking the link
        following each key point or by using our table of contents below to find
        the section you are looking for.
      </p>
      <ul>
        <li>
          <strong>What personal information do we process?</strong>
          <p>
            When you visit, use, or navigate our Services, we may process
            personal information depending on how you interact with us and the
            Services, the choices you make, and the products and features you
            use. Learn more about personal information you disclose to us.
          </p>
        </li>
        <li>
          <strong>Do we process any sensitive personal information?</strong>
          <p>
            Some of the information may be considered "special" or "sensitive"
            in certain jurisdictions, for example, your racial or ethnic
            origins, sexual orientation, and religious beliefs. We do not
            process sensitive personal information.
          </p>
        </li>
        <li>
          <strong>Do we collect any information from third parties?</strong>
          <p>We do not collect any information from third parties.</p>
        </li>
        <li>
          <strong>How do we process your information?</strong>
          <p>
            We process your information to provide, improve, and administer our
            Services, communicate with you, for security and fraud prevention,
            and to comply with the law. We may also process your information for
            other purposes with your consent. We process your information only
            when we have a valid legal reason to do so. Learn more about how we
            process your information.
          </p>
        </li>
        <li>
          <strong>
            In what situations and with which parties do we share personal
            information?
          </strong>
          <p>
            We may share information in specific situations and with specific
            third parties. Learn more about when and with whom we share your
            personal information.
          </p>
        </li>
        <li>
          <strong>How do we keep your information safe?</strong>
          <p>
            We have adequate organizational and technical processes and
            procedures in place to protect your personal information. However,
            no electronic transmission over the Internet or information storage
            technology can be guaranteed to be 100% secure, so we cannot promise
            or guarantee that hackers, cybercriminals, or other unauthorized
            third parties will not be able to defeat our security and improperly
            collect, access, steal, or modify your information. Learn more about
            how we keep your information safe.
          </p>
        </li>
        <li>
          <strong>What are your rights?</strong>
          <p>
            Depending on where you are located geographically, the applicable
            privacy law may mean you have certain rights regarding your personal
            information. Learn more about your privacy rights.
          </p>
        </li>
      </ul>
      <strong>How do you exercise your rights?</strong>
      <p>
        The easiest way to exercise your rights is by visiting
        support@getmoveapp.com, or by contacting us. We will consider and act
        upon any request in accordance with applicable data protection laws.
        Want to learn more about what we do with any information we collect?
        Review the Privacy Notice in full.
      </p>
      <h1>TABLE OF CONTENTS</h1>
      <ol>
        <li>WHAT INFORMATION DO WE COLLECT?</li>
        <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
        <li>
          WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
        </li>
        <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
        <li>WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</li>
        <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
        <li>HOW DO WE HANDLE YOUR SOCIAL LOGINS?</li>
        <li>IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</li>
        <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
        <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
        <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
        <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
        <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
        <li>DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
        <li>DO VIRGINIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
        <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
        <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
        <li>
          HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
        </li>
      </ol>
      <ol>
        <li>
          <strong>WHAT INFORMATION DO WE COLLECT?</strong>
          <strong>Personal information you disclose to us</strong>
          <p>
            <strong>In Short: </strong>
            <i>We collect personal information that you provide to us.</i>
          </p>
          <p>
            We collect personal information that you voluntarily provide to us
            when you register on the Services, express an interest in obtaining
            information about us or our products and Services, when you
            participate in activities on the Services, or otherwise when you
            contact us.
          </p>
          <p>
            <strong>Personal Information Provided by You</strong>
          </p>
          <p>
            The personal information that we collect depends on the context of
            your interactions with us and the Services, the choices you make,
            and the products and features you use. The personal information we
            collect may include the following:
          </p>
          <ul>
            <li>Names</li>
            <li>Phone numbers</li>
            <li>Email addresses</li>
            <li>Passwords</li>
            <li>Contact or authentication data</li>
          </ul>
          <br />
          <p>
            <strong>Sensitive Information</strong>
          </p>
          <p>We do not process sensitive information.</p>
          <p>
            <strong>Social Media Login Data</strong>
          </p>
          <p>
            We may provide you with the option to register with us using your
            existing social media account details, like your Facebook, X, or
            other social media account. If you choose to register in this way,
            we will collect certain profile information about you from the
            social media provider, as described in the section called "HOW DO WE
            HANDLE YOUR SOCIAL LOGINS?" below.
          </p>
          <p>
            <strong>Application Data</strong>
          </p>
          <p>
            If you use our application(s), we may also collect the following
            information if you choose to provide us with access or permission:
          </p>
          <ul>
            <li>
              <strong>Geolocation Information.</strong>
              <p>
                We may request access or permission to track location-based
                information from your mobile device, either continuously or
                while you are using our mobile application(s), to provide
                certain location-based services. If you wish to change our
                access or permissions, you may do so in your device's settings.
              </p>
            </li>
            <li>
              <strong>Mobile Device Access.</strong>
              <p>
                We may request access or permission to certain features from
                your mobile device, including your mobile device's reminders and
                other features. If you wish to change our access or permissions,
                you may do so in your device's settings.
              </p>
            </li>
            <li>
              <strong>Mobile Device Data.</strong>
              <p>
                We automatically collect device information (such as your mobile
                device ID, model, and manufacturer), operating system, version
                information, and system configuration information. This also
                includes device and application identification numbers, browser
                type and version, hardware model, Internet service provider,
                and/or mobile carrier, and Internet Protocol (IP) address (or
                proxy server). If you are using our application(s), we may also
                collect information about the phone network associated with your
                mobile device, your mobile device's operating system or
                platform, the type of mobile device you use, your mobile
                device's unique device ID, and information about the features of
                our application(s) you accessed.
              </p>
            </li>
            <li>
              <strong>Push Notifications.</strong>
              <p>
                We may request to send you push notifications regarding your
                account or certain features of the application(s). If you wish
                to opt out of receiving these types of communications, you may
                turn them off in your device's settings.
              </p>
            </li>
          </ul>
          <p>
            This information is primarily needed to maintain the security and
            operation of our application(s), for troubleshooting, and for our
            internal analytics and reporting purposes. All personal information
            that you provide to us must be true, complete, and accurate, and you
            must notify us of any changes to such personal information.
          </p>
          <p>
            <strong>Information Automatically Collected</strong>
          </p>
          <p>
            <strong>In Short: </strong>
            <i>
              Some information — such as your Internet Protocol (IP) address
              and/or browser and device characteristics — is collected
              automatically when you visit our Services.
            </i>
          </p>
          <p>
            We automatically collect certain information when you visit, use, or
            navigate the Services. This information does not reveal your
            specific identity (like your name or contact information) but may
            include device and usage information, such as your IP address,
            browser and device characteristics, operating system, language
            preferences, referring URLs, device name, country, location,
            information about how and when you use our Services, and other
            technical information. This information is primarily needed to
            maintain the security and operation of our Services and for our
            internal analytics and reporting purposes.
          </p>
          <p>
            <strong>The information we collect includes:</strong>
          </p>
          <ul>
            <li>
              <strong>Log and Usage Data.</strong>
              <p>
                Log and usage data is service-related, diagnostic, usage, and
                performance information our servers automatically collect when
                you access or use our Services and which we record in log files.
                Depending on how you interact with us, this log data may include
                your IP address, device information, browser type, and settings,
                as well as information about your activity in the Services (such
                as the date/time stamps associated with your usage, pages and
                files viewed, searches, and other actions you take, such as
                which features you use). Additionally, device event information
                (such as system activity, error reports, sometimes called "crash
                dumps", and hardware settings) may be collected.
              </p>
            </li>
            <li>
              <strong>Device Data.</strong>
              <p>
                We collect device data such as information about your computer,
                phone, tablet, or other device you use to access the Services.
                Depending on the device used, this data may include information
                such as your IP address (or proxy server), device and
                application identification numbers, location, browser type,
                hardware model, Internet service provider and/or mobile carrier,
                operating system, and system configuration information.
              </p>
            </li>
            <li>Location Data.</li>
            <p>
              We collect location data such as information about your device's
              location, which can be either precise or imprecise. How much
              information we collect depends on the type and settings of the
              device you use to access the Services. For example, we may use GPS
              and other technologies to collect geolocation data that tells us
              your current location (based on your IP address). You can opt out
              of allowing us to collect this information either by refusing
              access to the information or by disabling your Location setting on
              your device. However, if you choose to opt out, you may not be
              able to use certain aspects of the Services.
            </p>
          </ul>
          <p>
            <strong>Google API</strong>
          </p>
          <p>
            Our use of information received from Google APIs will adhere to the
            Google API Services User Data Policy, including the Limited Use
            requirements. Additionally, our Services include a messaging feature
            that allows users to communicate with each other. We may collect and
            store the messages exchanged between users for moderation purposes,
            security, and to enforce our community guidelines.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>How do we process your information?</strong>
          </p>
          <p>
            <strong>In Short: </strong>
            <i>
              We process your information to provide, improve, and administer
              our Services, communicate with you, for security and fraud
              prevention, and to comply with the law. We may also process your
              information for other purposes with your consent.
            </i>
          </p>
          <p>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </p>
          <ul>
            <li>
              <strong>
                To facilitate account creation and authentication and otherwise
                manage user accounts.
              </strong>
              <p>
                We may process your information so you can create and log in to
                your account, as well as keep your account in working order.
              </p>
            </li>
            <li>
              <strong>
                To deliver and facilitate the delivery of services to the user.
              </strong>
              <p>
                We may process your information to provide you with the
                requested service.
              </p>
            </li>
            <li>
              <strong>To send administrative information to you.</strong>
              <p>
                We may process your information to send you details about our
                products and services, changes to our terms and policies, and
                other similar information.
              </p>
            </li>
            <li>
              <strong>
                To save or protect an individual's vital interest.
              </strong>
              <p>
                We may process your information when necessary to save or
                protect an individual's vital interest, such as to prevent harm.
              </p>
            </li>
            <li>
              <strong>
                To monitor user interactions and enforce community guidelines.{" "}
              </strong>
              <p>
                We may review messages exchanged between users to ensure
                compliance with our terms of service. Users who engage in
                inappropriate behavior may face penalties, including temporary
                or permanent bans.
              </p>
            </li>
          </ul>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>
              WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
            </strong>
          </p>
          <p>
            <strong>In Short: </strong>
            <i>
              We only process your personal information when we believe it is
              necessary and we have a valid legal reason (i.e., legal basis) to
              do so under applicable law, like with your consent, to comply with
              laws, to provide you with services to enter into or fulfill our
              contractual obligations, to protect your rights, or to fulfill our
              legitimate business interests.
            </i>
          </p>
          <p>
            The{" "}
            <strong>
              General Data Protection Regulation (GDPR) and UK GDPR{" "}
            </strong>
            require us to explain the valid legal bases we rely on in order to
            process your personal information. As such, we may rely on the
            following legal bases to process your personal information:
          </p>
          <ul>
            <li>
              <strong>Consent.</strong>
              <p>
                We may process your information if you have given us permission
                (i.e., consent) to use your personal information for a specific
                purpose. You can withdraw your consent at any time. Learn more
                about withdrawing your consent.
              </p>
            </li>
            <li>
              <strong>Performance of a Contract</strong>
              <p>
                We may process your personal information when we believe it is
                necessary to fulfill our contractual obligations to you,
                including providing our Services or at your request prior to
                entering into a contract with you.
              </p>
            </li>
            <li>
              <strong>Legal Obligations.</strong>
              <p>
                We may process your information where we believe it is necessary
                for compliance with our legal obligations, such as to cooperate
                with a law enforcement body or regulatory agency, exercise or
                defend our legal rights, or disclose your information as
                evidence in litigation in which we are involved.
              </p>
            </li>
            <li>
              <strong>Vital Interests.</strong>
              <p>
                We may process your information where we believe it is necessary
                to protect your vital interests or the vital interests of a
                third party, such as situations involving potential threats to
                the safety of any person.
              </p>
            </li>
          </ul>
          <p>
            We also implement moderation systems to monitor user messages and
            interactions within our Services. If a user violates our community
            guidelines, we reserve the right to take appropriate actions,
            including temporary suspension or permanent banning of the user's
            account.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>
              WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </strong>
          </p>
          <p>
            <strong>In Short: </strong>
            <i>
              We may share information in specific situations described in this
              section and/or with the following third parties.
            </i>
          </p>
          <p>
            <strong>
              Vendors, Consultants, and Other Third-Party Service Providers
            </strong>
          </p>
          <p>
            We may share your data with third-party vendors, service providers,
            contractors, or agents ("third parties") who perform services for us
            or on our behalf and require access to such information to do that
            work. We have contracts in place with our third parties, which are
            designed to help safeguard your personal information. This means
            that they cannot do anything with your personal information unless
            we have instructed them to do so. They will also not share your
            personal information with any organization apart from us. They also
            commit to protecting the data they hold on our behalf and to
            retaining it for the period we instruct.
          </p>
          <p>
            <strong>
              The third parties we may share personal information with are as
              follows:
            </strong>
          </p>
          <ul>
            <li>
              <p>
                <strong>
                  Allow Users to Connect to Their Third-Party Accounts
                </strong>
              </p>
              <ul>
                <li>
                  <p>Google account, Facebook account, and Instagram account</p>
                </li>
              </ul>
            </li>
            <li>
              <p>
                <strong>Cloud Computing Services</strong>
              </p>
              <ul>
                <li>
                  <p>Amazon Web Services (AWS)</p>
                </li>
              </ul>
            </li>
            <li>
              <p>
                <strong>Data Backup and Security</strong>
              </p>
              <ul>
                <li>
                  <p>Amazon Glacier</p>
                </li>
              </ul>
            </li>
            <li>
              <p>
                <strong>Functionality and Infrastructure Optimization</strong>
              </p>
              <ul>
                <li>
                  <p>Firebase Hosting</p>
                </li>
              </ul>
            </li>
            <li>
              <p>
                <strong>User Account Registration and Authentication</strong>
              </p>
              <li>
                <p>
                  Google Sign-In, Facebook Login, and Instagram Authentication
                </p>
              </li>
            </li>
            <li>
              <p>
                <strong>Web and Mobile Analytics</strong>
              </p>
              <ul>
                <p>Google Analytics for Firebase</p>
              </ul>
            </li>
            <li>
              <p>
                <strong>Website Performance Monitoring</strong>
              </p>
              <ul>
                <li>
                  <p>
                    Firebase Performance Monitoring and Firebase Crash Reporting
                  </p>
                </li>
              </ul>
            </li>
          </ul>
          <p>
            <strong>
              Additional Situations Where We May Share Your Personal Information
            </strong>
          </p>
          <ul>
            <li>
              <p>
                <strong>Business Transfers.</strong>
                <br />
                We may share or transfer your information in connection with, or
                during negotiations of, any merger, sale of company assets,
                financing, or acquisition of all or a portion of our business to
                another company.
              </p>
            </li>
          </ul>
          <p>
            If a user is banned due to violations of our community guidelines,
            they may request a review of their case by contacting us at
            support@getmoveapp.com. However, we reserve the right to make
            final decisions regarding account suspensions.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>HOW DO WE HANDLE YOUR SOCIAL LOGINS?</strong>
          </p>
          <p>
            <strong>In Short: </strong>If you choose to register or log in to
            our Services using a social media account, we may have access to
            certain information about you.
          </p>
          <p>
            Our Services offer you the ability to register and log in using your
            third-party social media account details (like your Facebook or X
            logins). Where you choose to do this, we will receive certain
            profile information about you from your social media provider. The
            profile information we receive may vary depending on the social
            media provider concerned but will often include your name, email
            address, friends list, and profile picture, as well as other
            information you choose to make public on such a social media
            platform.
          </p>
          <p>
            We will use the information we receive only for the purposes that
            are described in this Privacy Notice or that are otherwise made
            clear to you on the relevant Services. Please note that we do not
            control, and are not responsible for, other uses of your personal
            information by your third-party social media provider. We recommend
            that you review their privacy notice to understand how they collect,
            use, and share your personal information, and how you can set your
            privacy preferences on their sites and apps.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>HOW LONG DO WE KEEP YOUR INFORMATION?</strong>
          </p>
          <p>
            <strong>In Short: </strong>We keep your information for as long as
            necessary to fulfill the purposes outlined in this Privacy Notice
            unless otherwise required by law.
          </p>
          <p>
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this Privacy Notice, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting, or other legal requirements). No purpose in this
            notice will require us to keep your personal information for longer
            than the period of time in which users have an account with us.
          </p>
          <p>
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymize such
            information, or, if this is not possible (for example, because your
            personal information has been stored in backup archives), then we
            will securely store your personal information and isolate it from
            any further processing until deletion is possible.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>HOW DO WE KEEP YOUR INFORMATION SAFE?</strong>
          </p>
          <p>
            <strong>In Short: </strong>We aim to protect your personal
            information through a system of organizational and technical
            security measures.
          </p>
          <p>
            We have implemented appropriate and reasonable technical and
            organizational security measures designed to protect the security of
            any personal information we process.
          </p>
          <p>
            However, despite our safeguards and efforts to secure your
            information, no electronic transmission over the Internet or
            information storage technology can be guaranteed to be 100% secure,
            so we cannot promise or guarantee that hackers, cybercriminals, or
            other unauthorized third parties will not be able to defeat our
            security and improperly collect, access, steal, or modify your
            information.
          </p>
          <p>
            Although we will do our best to protect your personal information,
            the transmission of personal information to and from our Services is
            at your own risk. You should only access the Services within a
            secure environment.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>WHAT ARE YOUR PRIVACY RIGHTS?</strong>
          </p>
          <p>
            <strong>In Short: </strong>In some regions, such as the European
            Economic Area (EEA), United Kingdom (UK), and Switzerland, you have
            rights that allow you greater access to and control over your
            personal information. You may review, change, or terminate your
            account at any time, depending on your country, province, or state
            of residence.
          </p>
          <p>
            In some regions (like the EEA, UK, and Switzerland), you have
            certain rights under applicable data protection laws. These may
            include the right:
          </p>
          <ol>
            <li>
              To request access and obtain a copy of your personal information.
            </li>
            <li>To request rectification or erasure.</li>
            <li>To restrict the processing of your personal information.</li>
            <li>If applicable, to data portability.</li>
            <li>Not to be subject to automated decision-making.</li>
          </ol>
          <p>
            In certain circumstances, you may also have the right to object to
            the processing of your personal information. You can make such a
            request by contacting us using the contact details provided in the
            section <strong>"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</strong>{" "}
            below.
          </p>
          <p>
            We will consider and act upon any request in accordance with
            applicable data protection laws.
          </p>
          <p>
            If you are located in the EEA or UK and you believe we are
            unlawfully processing your personal information, you also have the
            right to complain to your Member State data protection authority or
            UK data protection authority.
          </p>
          <p>
            If you are located in Switzerland, you may contact the Federal Data
            Protection and Information Commissioner.
          </p>
          <p>
            <strong>Withdrawing your consent:</strong>
          </p>
          <p>
            If we are relying on your consent to process your personal
            information, you have the right to withdraw your consent at any
            time. You can withdraw your consent at any time by contacting us
            using the contact details provided in the section{" "}
            <strong>"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</strong> below
            or by updating your preferences.
          </p>
          <p>
            However, please note that this will not affect the lawfulness of the
            processing before its withdrawal, nor will it affect the processing
            of your personal information conducted in reliance on lawful
            processing grounds other than consent.
          </p>
          <p>
            <strong>Account Information</strong>
          </p>
          <p>
            If you would at any time like to review or change the information in
            your account or terminate your account, you can:
          </p>
          <ul>
            <li>
              <p>
                <strong>
                  Log in to your account settings and update your user account.
                </strong>
              </p>
            </li>
            <li>
              <p>
                <strong>
                  Contact us using the contact information provided.
                </strong>
              </p>
            </li>
          </ul>
          <p>
            Upon your request to terminate your account, we will deactivate or
            delete your account and information from our active databases.
            However, we may retain some information in our files to prevent
            fraud, troubleshoot problems, assist with any investigations,
            enforce our legal terms, and/or comply with applicable legal
            requirements.
          </p>
          <p>
            If you have questions or comments about your privacy rights, you may
            email us at <strong>support@getmoveapp.com</strong>.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>CONTROLS FOR DO-NOT-TRACK FEATURES</strong>
          </p>
          <p>
            Most web browsers and some mobile operating systems and mobile
            applications include a <strong>Do-Not-Track</strong> ("DNT") feature
            or setting you can activate to signal your privacy preference not to
            have data about your online browsing activities monitored and
            collected.
          </p>
          <p>
            At this stage, no uniform technology standard for recognizing and
            implementing DNT signals has been finalized. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If
            a standard for online tracking is adopted that we must follow in the
            future, we will inform you about that practice in a revised version
            of this Privacy Notice.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>DO WE MAKE UPDATES TO THIS NOTICE?</strong>
          </p>
          <p>
            <strong>In Short: </strong>
            <i>
              Yes, we will update this notice as necessary to stay compliant
              with relevant laws.
            </i>
          </p>
          <p>
            We may update this Privacy Notice from time to time. The updated
            version will be indicated by an updated "<strong>Revised</strong>"
            date at the top of this Privacy Notice. If we make material changes
            to this Privacy Notice, we may notify you either by prominently
            posting a notice of such changes or by directly sending you a
            notification.
          </p>
          <p>
            We encourage you to review this Privacy Notice frequently to be
            informed of how we are protecting your information.
          </p>
        </li>
        <li>
          <p className="text-uppercase">
            <strong>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</strong>
          </p>
          <p>
            If you have questions or comments about this notice, you may email
            us at <strong>support@getmoveapp.com</strong> or contact us by
            post at:
          </p>
          <span>WebSite strategy di Giambrone Francesco</span>
          <br />
          <span>Corso Roma, 105</span>
          <br />
          <span>support@getmoveapp.com</span>
          <br />
          <span>Borgetto, PA 90042</span>
          <br />
          <span>Italy</span>
          <br />
        </li>
        <li>
          <p className="text-uppercase">
            <strong>
              HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </strong>
          </p>
          <p>
            Based on the applicable laws of your country, you may have the right
            to request access to the personal information we collect from you,
            details about how we have processed it, correct inaccuracies, or
            delete your personal information.
          </p>
          <p>
            You may also have the right to withdraw your consent to our
            processing of your personal information. These rights may be limited
            in some circumstances by applicable law.
          </p>
          <p>
            To request to review, update, or delete your personal information,
            please visit <strong>support@getmoveapp.com</strong>.
          </p>
        </li>
      </ol>
    </Container>
  );
};

export default PrivacyPolicy;
